import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, FileText, CheckCircle2, MessageSquare,
  Loader2, AlertCircle, Clock, DollarSign, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceDetail() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const navigate = useNavigate();
  const { serviceId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check auth
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsLoggedIn(true);
      try {
        setUser(JSON.parse(userData));
      } catch (e) {}
    }
    
    fetchServiceDetails();
  }, [serviceId]);

  const fetchServiceDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services/visa/${serviceId}`);
      if (response.data.success) {
        setService(response.data.service);
        setRequiredDocuments(response.data.required_documents || []);
      }
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error(isAr ? 'خطأ في جلب بيانات الخدمة' : 'Error fetching service details');
      navigate('/services/visa');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = () => {
    if (!isLoggedIn) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }
    
    // Navigate to chat with service info
    navigate(`/services/chat?service_id=${serviceId}&service_name=${encodeURIComponent(service?.name_ar || '')}`);
  };

  const formatPrice = (service) => {
    if (!service) return '';
    if (service.price_type === 'fixed') {
      return `${service.price?.toLocaleString()} ${isAr ? 'درهم' : 'AED'}`;
    } else if (service.price_type === 'range') {
      return `${service.price_min?.toLocaleString()} - ${service.price_max?.toLocaleString()} ${isAr ? 'درهم' : 'AED'}`;
    }
    return isAr ? 'حسب الطلب' : 'On request';
  };

  const formatDuration = (service) => {
    if (!service) return '';
    if (service.duration_type === 'fixed' && service.duration) {
      return `${service.duration} ${isAr ? 'يوم عمل' : 'business days'}`;
    } else if (service.duration_type === 'range' && service.duration_min) {
      return `${service.duration_min} - ${service.duration_max} ${isAr ? 'يوم عمل' : 'business days'}`;
    } else if (service.duration_days) {
      return `${service.duration_days} ${isAr ? 'يوم عمل' : 'business days'}`;
    }
    return isAr ? 'حسب الخدمة' : 'Varies';
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            {isAr ? 'جاري التحميل...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            {isAr ? 'الخدمة غير موجودة' : 'Service not found'}
          </p>
          <Button onClick={() => navigate('/services/visa')} className="mt-4">
            {isAr ? 'العودة للخدمات' : 'Back to Services'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${isDarkMode ? 'bg-[#0d0d0d]/95 backdrop-blur-sm border-white/5' : 'bg-white/95 backdrop-blur-sm border-gray-200'} border-b`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/services/visa')}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}
              data-testid="back-button"
            >
              {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            </button>
            <div>
              <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {isAr ? 'تفاصيل الخدمة' : 'Service Details'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Service Info Card */}
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#1a1a1a] border border-white/5' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
              <FileText className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {isAr ? service.name_ar : (service.name_en || service.name_ar)}
              </h2>
              {service.description && (
                <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {service.description}
                </p>
              )}
              
              {/* Price & Duration */}
              <div className="flex flex-wrap gap-4">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-green-500/10' : 'bg-green-50'}`}>
                  <DollarSign className="h-4 w-4 text-green-500" />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                    {formatPrice(service)}
                  </span>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                    {formatDuration(service)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Required Documents Card */}
        <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-[#1a1a1a] border border-white/5' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {isAr ? 'الوثائق المطلوبة' : 'Required Documents'}
              </h3>
              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {isAr ? 'يجب أن تكون هذه الوثائق جاهزة قبل البدء' : 'These documents must be ready before starting'}
              </p>
            </div>
          </div>

          {requiredDocuments.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {requiredDocuments.map((doc, index) => (
                <div 
                  key={doc.id || index}
                  className={`flex items-center gap-3 p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {isAr ? doc.name_ar : (doc.name_en || doc.name_ar)}
                    </p>
                    {doc.description && (
                      <p className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {doc.description}
                      </p>
                    )}
                  </div>
                  {doc.is_required !== false && (
                    <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                      {isAr ? 'مطلوب' : 'Required'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">
                {isAr ? 'سيتم تحديد الوثائق المطلوبة أثناء المحادثة' : 'Required documents will be specified during the conversation'}
              </p>
            </div>
          )}
        </div>

        {/* User Status Card */}
        {!isLoggedIn && (
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-red-500/10 border border-red-500/20' : 'bg-red-50 border border-red-200'}`}>
            <div className="flex items-center gap-3">
              <AlertCircle className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                  {isAr ? 'يجب تسجيل الدخول لبدء الطلب' : 'Please login to start the request'}
                </p>
              </div>
              <Button 
                onClick={() => navigate('/login')}
                size="sm"
                className="bg-red-500 hover:bg-red-600"
              >
                {isAr ? 'تسجيل الدخول' : 'Login'}
              </Button>
            </div>
          </div>
        )}

        {isLoggedIn && user && (
          <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
            <div className="flex items-center gap-3">
              <CheckCircle2 className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  {isAr ? `مسجل الدخول كـ: ${user.name || user.email}` : `Logged in as: ${user.name || user.email}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Start Button */}
        <div className="pt-4">
          <Button
            onClick={handleStartChat}
            disabled={!isLoggedIn}
            className="w-full py-6 text-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50"
            data-testid="start-ai-chat"
          >
            <Sparkles className="h-5 w-5 me-2" />
            {isAr ? 'التحدث مع المساعد الذكي' : 'Talk to AI Assistant'}
          </Button>
          
          <p className={`text-center text-xs mt-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {isAr 
              ? 'المساعد الذكي سيساعدك في إكمال الطلب وجمع الوثائق المطلوبة' 
              : 'The AI assistant will help you complete the request and collect required documents'}
          </p>
        </div>
      </div>
    </div>
  );
}
